import * as React from 'react';
import { useUnmount } from 'react-use';
import { useQuery } from 'react-query';
import hoistNonReactStatic from 'hoist-non-react-statics';
import {
  AudioConfig,
  CancellationReason,
  PronunciationAssessmentConfig,
  PronunciationAssessmentGradingSystem,
  PronunciationAssessmentGranularity,
  PronunciationAssessmentResult,
  ResultReason,
  SpeechConfig,
  SpeechRecognitionResult,
  SpeechRecognizer,
} from 'microsoft-cognitiveservices-speech-sdk';

import {
  LISTENING_STATE,
  ListeningStateModel,
} from '@doltech/utils/lib/voice-recognition/constants';
import { publishEvent, subscribeEvent } from '@doltech/utils/lib/events';
import { mapSentenceWithAbbreviationToFullWord, splitSentenceToWords } from '@doltech/utils/lib/sentence';

import { NativeSpeechRecognition } from '../utils/NativeSpeechRecognition';
import { AssessmentConfigModel } from '../utils/assessment-type';
import { AssessmentResultModel, mapAssessmentResult } from '../utils/assessment-mapping.util';
import { getSpeechToTextToken } from '../api/speech-to-text';

const REGION = 'southeastasia';
const EVENT_SESSION_FINISHED = 'sessionFinished';
const DEFAULT_ASSESSMENT_CONFIG: AssessmentConfigModel = {
  enableMiscue: true,
};

interface StopListeningState {
  assessmentResult?: AssessmentResultModel;
}

interface PronunciationAssessmentModel {
  speechRecognizer?: SpeechRecognizer;
  listeningState?: ListeningStateModel;
  transcript?: string;
  speechRecognized?: string;
  transcriptIntegrating?: string;
  rawAssessmentResults?: PronunciationAssessmentResult[];
  assessmentResult?: AssessmentResultModel;
  startListening?: (referenceText: string, inputStream?: MediaStream, config?: AssessmentConfigModel) => Promise<void>;
  stopListening?: () => Promise<StopListeningState>;
  cancelListening?: () => Promise<void>;
  resetTranscript?: () => void;
  resetAssessmentResult?: () => void;
  resetAll?: () => void;
  listening?: boolean;
  browserSupportsSpeechRecognition?: boolean;
}

const PronunciationAssessmentContext = React.createContext<PronunciationAssessmentModel>({});

export const usePronunciationAssessment = (): PronunciationAssessmentModel =>
  React.useContext(PronunciationAssessmentContext);

export const withPronunciationAssessment = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const ResponseComponent = (props: P) => {
    const [listeningState, setListeningState] = React.useState<ListeningStateModel>(
      LISTENING_STATE.NOT_STARTED
    );
    const [speechRecognizer, setSpeechRecognizer] = React.useState<SpeechRecognizer>(null);
    const [speechResults, setSpeechResults] = React.useState<SpeechRecognitionResult[]>([]);
    const [speechRecognized, setSpeechRecognized] = React.useState<SpeechRecognitionResult[]>([]);
    const [transcriptIntegrating, setTranscriptIntegrating] = React.useState<
      SpeechRecognitionResult[]
    >([]);
    const [assessmentConfig, setAssessmentConfig] = React.useState<AssessmentConfigModel>(null);
    const [assessmentReferenceText, setAssessmentReferenceText] = React.useState<string>(null);
    const [rawAssessmentResults, setRawAssessmentResults] = React.useState<
      PronunciationAssessmentResult[]
    >([]);

    const { data } = useQuery(['SPEECH_TO_TEXT_TOKEN'], () => getSpeechToTextToken());

    React.useEffect(() => {
      const rawResults = [];

      if (speechRecognizer) {
        speechRecognizer.recognizing = (s, e) => {
          // RECOGNIZING: Text=${e.result.text}

          setTranscriptIntegrating([e.result]);
          setSpeechResults((prevSpeechResults) => {
            const existResult = prevSpeechResults.find(
              (eachResult) => eachResult.offset === e.result.offset
            );

            if (existResult) {
              return prevSpeechResults.map((eachResult) => {
                if (eachResult.offset === e.result.offset) {
                  return e.result;
                }
                return eachResult;
              });
            }

            return [...prevSpeechResults, e.result];
          });
        };

        speechRecognizer.recognized = (s, e) => {
          if (e.result.reason === ResultReason.RecognizedSpeech) {
            // RECOGNIZED: Text=${e.result.text}
            setSpeechResults((prevSpeechResults) => {
              return prevSpeechResults.map((eachResult) => {
                if (eachResult.offset === e.result.offset) {
                  return e.result;
                }
                return eachResult;
              });
            });
            setSpeechRecognized([e.result]);
            setTranscriptIntegrating([]);
            const pronunciationAssessmentResult = PronunciationAssessmentResult.fromResult(
              e.result
            );
            rawResults.push(pronunciationAssessmentResult);
          } else if (e.result.reason === ResultReason.NoMatch) {
            console.log('NOMATCH: Speech could not be recognized.');
          }
        };

        speechRecognizer.canceled = (s, e) => {
          console.log(`CANCELED: Reason=${e.reason}`);

          if (e.reason === CancellationReason.Error) {
            console.log(`"CANCELED: ErrorCode=${e.errorCode}`);
            console.log(`"CANCELED: ErrorDetails=${e.errorDetails}`);
            console.log('CANCELED: Did you set the speech resource key and region values?');
          }

          setListeningState(LISTENING_STATE.STOPPED);
          speechRecognizer.stopContinuousRecognitionAsync();
        };

        speechRecognizer.sessionStopped = (s) => {
          console.log('\n    Session stopped event.');
          setListeningState(LISTENING_STATE.STOPPED);
          setRawAssessmentResults(rawResults);
          speechRecognizer.stopContinuousRecognitionAsync();
          publishEvent(EVENT_SESSION_FINISHED, { rawResults });
        };
      }
    }, [speechRecognizer]);

    const initRecognizer = (
      referenceText: string,
      inputStream?: MediaStream,
      config: AssessmentConfigModel = DEFAULT_ASSESSMENT_CONFIG,
    ): Promise<{
      recognizer: SpeechRecognizer;
      audioConfig: AudioConfig;
      pronunciationAssessmentConfig: PronunciationAssessmentConfig;
    }> => {
      return new Promise((resolve) => {
        setAssessmentReferenceText(referenceText);
        setAssessmentConfig(config);
        const speechConfig = SpeechConfig.fromAuthorizationToken(data?.token, REGION);
        speechConfig.speechRecognitionLanguage = 'en-US';
        const pronunciationAssessmentConfig = new PronunciationAssessmentConfig(
          mapSentenceWithAbbreviationToFullWord(referenceText),
          PronunciationAssessmentGradingSystem.HundredMark,
          PronunciationAssessmentGranularity.Phoneme,
          false
        );
        pronunciationAssessmentConfig.enableProsodyAssessment = true;
        pronunciationAssessmentConfig.nbestPhonemeCount = 5;
        pronunciationAssessmentConfig.enableContentAssessmentWithTopic('');

        let audioConfig: AudioConfig = AudioConfig.fromDefaultMicrophoneInput();

        if (inputStream) {
          audioConfig = AudioConfig.fromStreamInput(inputStream);
        }

        const recognizer = new SpeechRecognizer(speechConfig, audioConfig);
        pronunciationAssessmentConfig.applyTo(recognizer);

        setSpeechRecognizer(recognizer);

        resolve({
          recognizer,
          audioConfig,
          pronunciationAssessmentConfig,
        });
      });
    };

    const startListening = async (
      referenceText: string,
      inputStream: MediaStream,
      config?: AssessmentConfigModel
    ): Promise<void> => {
      const convertedReferenceText = splitSentenceToWords(referenceText)?.join(' ');
      const { recognizer } = await initRecognizer(convertedReferenceText, inputStream, config);
      recognizer.startContinuousRecognitionAsync();

      return new Promise<void>((resolve) => {
        recognizer.sessionStarted = () => {
          console.log('\n    Session start event.');
          setListeningState(LISTENING_STATE.LISTENING);
          resolve();
        };
      });
    };

    const stopListening = async () => {
      speechRecognizer?.stopContinuousRecognitionAsync();

      return new Promise<StopListeningState>((resolve) => {
        const sessionFinishedHandler = (e) => {
          resolve({
            assessmentResult: mapAssessmentResult(e.detail?.rawResults, assessmentReferenceText, assessmentConfig),
          });
        };
        subscribeEvent(EVENT_SESSION_FINISHED, sessionFinishedHandler);
      });
    };

    const cancelListening = async () => {
      speechRecognizer?.stopContinuousRecognitionAsync();
      setListeningState(LISTENING_STATE.STOPPED);
    };

    const resetTranscript = () => {
      setSpeechResults([]);
    };

    const resetAssessmentResult = () => {
      setRawAssessmentResults([]);
    };

    const resetAll = () => {
      resetTranscript();
      resetAssessmentResult();
      setSpeechRecognizer(null);
    };

    useUnmount(() => {
      stopListening();
    });

    return (
      <PronunciationAssessmentContext.Provider
        value={{
          speechRecognizer,
          listeningState,
          rawAssessmentResults,
          assessmentResult: mapAssessmentResult(rawAssessmentResults, assessmentReferenceText, assessmentConfig),
          transcript: speechResults.map((eachResult) => eachResult.text).join(' '),
          speechRecognized: speechRecognized.map((eachResult) => eachResult.text).join(' '),
          transcriptIntegrating: transcriptIntegrating.map((eachResult) => eachResult.text).join(' '),
          startListening,
          stopListening,
          cancelListening,
          resetTranscript,
          resetAssessmentResult,
          resetAll,
          listening: LISTENING_STATE.LISTENING === listeningState,
          browserSupportsSpeechRecognition: Boolean(NativeSpeechRecognition),
        }}
      >
        <Component {...props} />
      </PronunciationAssessmentContext.Provider>
    );
  };

  hoistNonReactStatic(ResponseComponent, Component);

  return ResponseComponent;
};
